import React, { useState } from "react";
import {
  User,
  Stethoscope,
  Calendar,
  CreditCard,
  XCircle,
  RefreshCw,
  FileText,
  CircleCheck,
  Pencil,
  RotateCcw,
  Printer,
} from "lucide-react";
import { AppointmentSummaryResponse } from "./types";
import toast from "react-hot-toast";
import { AppointmentService } from "../../../services/api/appointment.service";
import { UpdatePendingPaymentModal } from "./modals/UpdatePendingPaymentModal";
import { UpdatePaymentMethodModal } from "./modals/UpdatePaymentMethodModal";
import { RescheduleModal } from "./modals/RescheduleModal";
import { CompleteAppointmentModal } from "./modals/CompleteAppointmentModal";
import { CancelAppointmentModal } from "./modals/CancelAppointmentModal";
import { EditPatientModal } from "./modals/EditPatientModal";
import { MediaViewerModal } from "./modals/MediaViewerModal";
import { SamplePrescriptionModal } from "./modals/SamplePrescriptionModal";

interface SummaryTabProps {
  data: AppointmentSummaryResponse["appointmentSummary"];
  reload: () => void;
}

const SummaryTab: React.FC<SummaryTabProps> = ({ data, reload }) => {
  const { appointment, doctorDetails, appointmentSummary, doctorPayments, medias } =
    data;
  const paymentTransactions = (data as any).paymentTransactions || [];
  const doctor = doctorDetails[0];
  const patient = appointmentSummary.patient;

  // Modals state
  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
  const [mediaTitle, setMediaTitle] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");

  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isEditPatientModalOpen, setIsEditPatientModalOpen] = useState(false);
  const [isUpdatePendingModalOpen, setIsUpdatePendingModalOpen] = useState(false);
  const [isUpdatePaymentMethodModalOpen, setIsUpdatePaymentMethodModalOpen] = useState(false);
  const [isSamplePrescriptionModalOpen, setIsSamplePrescriptionModalOpen] = useState(false);

  const [isUndoingCancel, setIsUndoingCancel] = useState(false);

  const handleUndoCancellation = async () => {
    setIsUndoingCancel(true);
    try {
      const response = await AppointmentService.markInConsultation(appointment.id);
      if (response.success) {
        toast.success(response.message || "Appointment reactivated successfully");
        reload();
      } else {
        toast.error(response.message || "Failed to reactivate appointment");
      }
    } catch (error) {
      toast.error("Failed to reactivate appointment");
    } finally {
      setIsUndoingCancel(false);
    }
  };

  const handleViewBill = () => {
    const billUrl = medias?.[0]?.bill_url;
    if (billUrl) {
      setMediaTitle("View Bill");
      setMediaUrl(billUrl);
      setIsMediaModalOpen(true);
    } else {
      toast.error("No bill available for this appointment.");
    }
  };

  const handleViewPrescription = () => {
    const presUrl = medias?.[0]?.prescription_url;
    if (presUrl) {
      setMediaTitle("View Prescription");
      setMediaUrl(presUrl);
      setIsMediaModalOpen(true);
    } else {
      toast.error("No prescription available for this appointment.");
    }
  };

  const statusLower = (appointment.status || "").toLowerCase();
  const paymentStatusLower = (appointment.paymentStatus || doctorPayments?.status || "").toLowerCase();

  const isUpcoming =
    statusLower === "booked" ||
    statusLower === "in consultation" ||
    statusLower === "in_consultation" ||
    statusLower === "upcoming";

  const isCompleted = statusLower === "completed";
  const isCancelled = statusLower === "cancelled";

  const hasPendingPayment = Boolean(
    (doctorPayments?.balance_due && doctorPayments.balance_due > 0) ||
    paymentStatusLower === "pending" ||
    paymentStatusLower === "partially_paid" ||
    paymentStatusLower === "partially paid" ||
    paymentStatusLower.includes("partially") ||
    paymentStatusLower.includes("pending") ||
    statusLower.includes("pending")
  );

  const hasRecordedPayment = Boolean(
    doctorPayments &&
    ((Number(doctorPayments.cash_amount) || 0) > 0 ||
      (Number(doctorPayments.online_amount) || 0) > 0 ||
      (Number(doctorPayments.advance_amount) || 0) > 0 ||
      (Number(doctorPayments.amount) || 0) > 0)
  );

  const isUpcomingOrPending = (isUpcoming || hasPendingPayment || statusLower.includes("pending")) && !isCompleted && !isCancelled;

  return (
    <div className="space-y-8">
      {/* Action Buttons Bar for Upcoming and Payment Pending Appointments */}
      {isUpcomingOrPending && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-gray-50/80 p-3 rounded-2xl border border-gray-100 mb-2">
          <div className="flex items-center gap-2 px-2">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Actions</span>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2.5">
            {hasPendingPayment && (
              <button
                onClick={() => setIsUpdatePendingModalOpen(true)}
                className="flex items-center gap-2 px-3.5 py-2 bg-amber-50 text-amber-700 border border-amber-200/80 hover:bg-amber-100 rounded-xl text-xs font-semibold transition-all shadow-2xs cursor-pointer"
              >
                <CreditCard className="h-4 w-4 text-amber-600" /> Update Pending Payment
              </button>
            )}
            {hasRecordedPayment && (
              <button
                onClick={() => setIsUpdatePaymentMethodModalOpen(true)}
                className="flex items-center gap-2 px-3.5 py-2 bg-indigo-50 text-indigo-700 border border-indigo-200/80 hover:bg-indigo-100 rounded-xl text-xs font-semibold transition-all shadow-2xs cursor-pointer"
              >
                <CreditCard className="h-4 w-4 text-indigo-600" /> Update Payment Method
              </button>
            )}
            <button
              onClick={() => setIsSamplePrescriptionModalOpen(true)}
              className="flex items-center gap-2 px-3.5 py-2 bg-emerald-50 text-emerald-700 border border-emerald-200/80 hover:bg-emerald-100 rounded-xl text-xs font-semibold transition-all shadow-2xs cursor-pointer"
            >
              <Printer className="h-4 w-4 text-emerald-600" /> Print Sample Prescription
            </button>
            <button
              onClick={handleViewBill}
              className="flex items-center gap-2 px-3.5 py-2 bg-purple-50 text-purple-700 border border-purple-200/80 hover:bg-purple-100 rounded-xl text-xs font-semibold transition-all shadow-2xs cursor-pointer"
            >
              <CreditCard className="h-4 w-4 text-purple-600" /> View Bill
            </button>
            {isUpcoming && (
              <button
                onClick={() => setIsCompleteModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm active:scale-95 cursor-pointer"
              >
                <CircleCheck className="h-4 w-4" /> Complete Appointment
              </button>
            )}
            {isUpcoming && (
              <button
                onClick={() => setIsRescheduleModalOpen(true)}
                className="flex items-center gap-2 px-3.5 py-2 bg-sky-50 text-sky-700 border border-sky-200/80 hover:bg-sky-100 rounded-xl text-xs font-semibold transition-all shadow-2xs cursor-pointer"
              >
                <RefreshCw className="h-4 w-4 text-sky-600" /> Reschedule
              </button>
            )}
            <button
              onClick={() => setIsCancelModalOpen(true)}
              className="flex items-center gap-2 px-3.5 py-2 bg-rose-50 text-rose-700 border border-rose-200/80 hover:bg-rose-100 rounded-xl text-xs font-semibold transition-all shadow-2xs cursor-pointer"
            >
              <XCircle className="h-4 w-4 text-rose-600" /> Cancel
            </button>
          </div>
        </div>
      )}

      {isCompleted && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-gray-50/80 p-3 rounded-2xl border border-gray-100 mb-2">
          <div className="flex items-center gap-2 px-2">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Actions</span>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2.5">
            {hasRecordedPayment && (
              <button
                onClick={() => setIsUpdatePaymentMethodModalOpen(true)}
                className="flex items-center gap-2 px-3.5 py-2 bg-indigo-50 text-indigo-700 border border-indigo-200/80 hover:bg-indigo-100 rounded-xl text-xs font-semibold transition-all shadow-2xs cursor-pointer"
              >
                <CreditCard className="h-4 w-4 text-indigo-600" /> Update Payment Method
              </button>
            )}
            <button
              onClick={() => setIsSamplePrescriptionModalOpen(true)}
              className="flex items-center gap-2 px-3.5 py-2 bg-emerald-50 text-emerald-700 border border-emerald-200/80 hover:bg-emerald-100 rounded-xl text-xs font-semibold transition-all shadow-2xs"
            >
              <Printer className="h-4 w-4 text-emerald-600" /> Print Sample Prescription
            </button>
            <button
              onClick={handleViewBill}
              className="flex items-center gap-2 px-3.5 py-2 bg-purple-50 text-purple-700 border border-purple-200/80 hover:bg-purple-100 rounded-xl text-xs font-semibold transition-all shadow-2xs"
            >
              <CreditCard className="h-4 w-4 text-purple-600" /> View Bill
            </button>
            <button
              onClick={handleViewPrescription}
              className="flex items-center gap-2 px-3.5 py-2 bg-sky-50 text-sky-700 border border-sky-200/80 hover:bg-sky-100 rounded-xl text-xs font-semibold transition-all shadow-2xs"
            >
              <FileText className="h-4 w-4 text-sky-600" /> View Prescription
            </button>
          </div>
        </div>
      )}

      {(appointment.status === "Cancelled" || appointment.status?.toLowerCase() === "cancelled") && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-gray-50/80 p-3 rounded-2xl border border-gray-100 mb-2">
          <div className="flex items-center gap-2 px-2">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Actions</span>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2.5">
            <button
              onClick={() => setIsSamplePrescriptionModalOpen(true)}
              className="flex items-center gap-2 px-3.5 py-2 bg-emerald-50 text-emerald-700 border border-emerald-200/80 hover:bg-emerald-100 rounded-xl text-xs font-semibold transition-all shadow-2xs"
            >
              <Printer className="h-4 w-4 text-emerald-600" /> Print Sample Prescription
            </button>
            <button
              onClick={handleUndoCancellation}
              disabled={isUndoingCancel}
              className="flex items-center gap-2 px-3.5 py-2 bg-amber-50 text-amber-700 border border-amber-200/80 hover:bg-amber-100 rounded-xl text-xs font-semibold transition-all shadow-2xs disabled:opacity-50"
            >
              <RotateCcw className={`h-4 w-4 text-amber-600 ${isUndoingCancel ? "animate-spin" : ""}`} />
              {isUndoingCancel ? "Reactivating..." : "Undo Cancellation"}
            </button>
          </div>
        </div>
      )}

      {/* Grid Content Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Patient Details */}
        <section className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <User className="h-5 w-5 text-emerald-600" />
              <h2 className="text-lg font-bold text-gray-900">Patient Information</h2>
            </div>
            {isUpcomingOrPending && (
              <button
                onClick={() => setIsEditPatientModalOpen(true)}
                className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors cursor-pointer"
                title="Edit Patient Details"
              >
                <Pencil className="h-5 w-5" />
              </button>
            )}
          </div>
          <div className="space-y-4">
            <InfoRow label="Name" value={patient.name} />
            <InfoRow label="Patient Id" value={patient.dhpId} />
            <InfoRow label="Phone" value={patient.mobileNumber} />
            <InfoRow label="Gender" value={patient.gender} />
            <InfoRow label="Age" value={patient.age} />
            <InfoRow label="Concern" value={appointment.patientConcern} />
          </div>
        </section>

        {/* Doctor Details */}
        <section className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <Stethoscope className="h-5 w-5 text-emerald-600" />
            <h2 className="text-lg font-bold text-gray-900">Doctor Information</h2>
          </div>
          <div className="space-y-4">
            <InfoRow label="Name" value={doctor.name} />
            <InfoRow label="Registration" value={doctor.registrationNumber} />
            <InfoRow label="Experience" value={`${doctor.experience} Years`} />
            <InfoRow label="Hospital" value={doctor.hospitalName} />
            <InfoRow label="Education" value={doctor.education.join(", ")} />
            <InfoRow label="Specialization" value={doctor.specialization.join(", ")} />
          </div>
        </section>

        {/* Appointment Basics */}
        <section className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <Calendar className="h-5 w-5 text-emerald-600" />
            <h2 className="text-lg font-bold text-gray-900">Appointment Details</h2>
          </div>
          <div className="space-y-4">
            <InfoRow
              label="Date"
              value={new Date(appointment.appointmentDate).toLocaleDateString()}
            />
            <InfoRow label="Time" value={`${appointment.startTime} - ${appointment.endTime}`} />
            <InfoRow label="Type" value={appointment.consultationType} />
            <InfoRow label="Status" value={appointment.status} />
          </div>
        </section>

        {/* Billing & Payment Details */}
        <section className="bg-gray-50 rounded-2xl p-6 border border-gray-100 lg:col-span-2">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <CreditCard className="h-5 w-5 text-emerald-600" />
              <h2 className="text-lg font-bold text-gray-900">Billing & Payment Summary</h2>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {hasRecordedPayment && (
                <button
                  onClick={() => setIsUpdatePaymentMethodModalOpen(true)}
                  className="flex items-center gap-2 px-3.5 py-2 bg-indigo-50 text-indigo-700 border border-indigo-200/80 hover:bg-indigo-100 rounded-xl text-xs font-semibold shadow-2xs transition-colors cursor-pointer"
                >
                  <CreditCard className="h-4 w-4 text-indigo-600" /> Update Payment Method
                </button>
              )}
              {Boolean((doctorPayments?.balance_due && doctorPayments.balance_due > 0) || (doctorPayments?.status || "").toLowerCase().includes("partially")) && (
                <button
                  onClick={() => setIsUpdatePendingModalOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-semibold shadow-sm transition-colors cursor-pointer"
                >
                  <CreditCard className="h-4 w-4" /> Update Pending Payment
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="space-y-3 bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
              <InfoRow label="Total Amount" value={`₹${doctorPayments?.amount ?? 0}`} />
              <InfoRow label="Advance Paid" value={`₹${doctorPayments?.advance_amount ?? 0}`} />
              <InfoRow label="Cash Paid" value={`₹${doctorPayments?.cash_amount ?? 0}`} />
              <InfoRow label="Online Paid" value={`₹${doctorPayments?.online_amount ?? 0}`} />
            </div>
            <div className="space-y-3 bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
              <InfoRow label="Balance Due" value={`₹${doctorPayments?.balance_due ?? 0}`} />
              <InfoRow
                label="Payment Status"
                value={doctorPayments?.status || appointment.paymentStatus || "Pending"}
              />
              <InfoRow
                label="Payment Type"
                value={doctorPayments?.is_split ? "Split" : (appointment.paymentType || "N/A")}
              />
              <InfoRow
                label="Payment Mode"
                value={`${doctorPayments?.is_advance ? "Advance" : "Full Payment"} (${doctorPayments?.is_split ? "Split" : "Single"})`}
              />
            </div>
          </div>

          {/* Payment Transactions Table */}
          {paymentTransactions && paymentTransactions.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Payment Transactions Breakdown</h3>
              <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
                <table className="min-w-full divide-y divide-gray-200 text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase">Txn Key</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase">Type</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase">Method</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase">Amount</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase">Date</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {paymentTransactions.map((txn: any) => (
                      <tr key={txn.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-600 font-mono text-[11px]">{txn.idempotent_key || txn.id}</td>
                        <td className="px-3 py-2 text-gray-800 capitalize font-medium">{txn.txn_type}</td>
                        <td className="px-3 py-2 text-gray-800 capitalize font-medium">{txn.payment_method}</td>
                        <td className="px-3 py-2 text-emerald-700 font-bold">₹{txn.amount}</td>
                        <td className="px-3 py-2 text-gray-500">
                          {txn.payment_date ? new Date(txn.payment_date).toLocaleString() : "-"}
                        </td>
                        <td className="px-3 py-2">
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-800 capitalize">
                            {txn.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      </div>

      {/* Extracted Modals */}
      <MediaViewerModal
        isOpen={isMediaModalOpen}
        onClose={() => setIsMediaModalOpen(false)}
        title={mediaTitle}
        url={mediaUrl}
      />

      <RescheduleModal
        isOpen={isRescheduleModalOpen}
        onClose={() => setIsRescheduleModalOpen(false)}
        appointment={appointment}
        reload={reload}
      />

      <CompleteAppointmentModal
        isOpen={isCompleteModalOpen}
        onClose={() => setIsCompleteModalOpen(false)}
        appointment={appointment}
        doctor={doctor}
        patient={patient}
        reload={reload}
      />

      <CancelAppointmentModal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        appointment={appointment}
        reload={reload}
      />

      <EditPatientModal
        isOpen={isEditPatientModalOpen}
        onClose={() => setIsEditPatientModalOpen(false)}
        patient={patient}
        reload={reload}
      />

      <UpdatePendingPaymentModal
        isOpen={isUpdatePendingModalOpen}
        onClose={() => setIsUpdatePendingModalOpen(false)}
        appointmentId={appointment.id}
        doctorPayments={doctorPayments}
        reload={reload}
      />

      <UpdatePaymentMethodModal
        isOpen={isUpdatePaymentMethodModalOpen}
        onClose={() => setIsUpdatePaymentMethodModalOpen(false)}
        appointmentId={appointment.id}
        doctorPayments={doctorPayments}
        dhrId={appointment.dhrId}
        reload={reload}
      />

      <SamplePrescriptionModal
        isOpen={isSamplePrescriptionModalOpen}
        onClose={() => setIsSamplePrescriptionModalOpen(false)}
        appointmentId={appointment.id}
        doctor={doctor}
        patient={patient}
        appointment={appointment}
        vitals={appointmentSummary.vitals}
        medications={appointmentSummary.medications}
        diagnostics={appointmentSummary.diagnostics}
      />
    </div>
  );
};

const InfoRow = ({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) => (
  <div className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0 hover:bg-white/50 transition-colors px-2 rounded-lg">
    <span className="text-sm font-medium text-gray-500">{label}</span>
    <span className="text-sm font-bold text-gray-900 capitalize">
      {value || "N/A"}
    </span>
  </div>
);

export default SummaryTab;
